import { getUiLocale } from '../../../../utils/uiTranslation';
import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set, update } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../../services/nationalizationService';

const PAY_SCHEDULES = ['monthly', 'biweekly', 'weekly', 'bi_monthly', 'annual'];
const CURRENCIES = [
  'AED', 'SAR', 'QAR', 'OMR', 'BHD', 'KWD',
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'INR', 'BRL', 'MXN', 'ZAR'
];

const EditCompensationModal = ({ employeeId, companyUID, employeeName, currentCompensation, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [baseSalary, setBaseSalary] = useState(currentCompensation?.base || '');
  const [currency, setCurrency] = useState(currentCompensation?.currency || 'USD');
  const [paySchedule, setPaySchedule] = useState(currentCompensation?.paySchedule || 'monthly');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [allowances, setAllowances] = useState(currentCompensation?.allowances || []);
  const [deductions, setDeductions] = useState(currentCompensation?.deductions || []);
  const [saving, setSaving] = useState(false);
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  // Set default effective date to today
  React.useEffect(() => {
    const today = new Date();
    setEffectiveDate(today.toISOString().split('T')[0]);
  }, []);

  const addAllowance = () => {
    setAllowances([...allowances, { name: '', amount: 0, type: 'allowance' }]);
  };

  const removeAllowance = (index) => {
    setAllowances(allowances.filter((_, i) => i !== index));
  };

  const updateAllowance = (index, field, value) => {
    const updated = [...allowances];
    updated[index] = { ...updated[index], [field]: value };
    setAllowances(updated);
  };

  const addDeduction = () => {
    setDeductions([...deductions, { name: '', amount: 0, type: 'deduction' }]);
  };

  const removeDeduction = (index) => {
    setDeductions(deductions.filter((_, i) => i !== index));
  };

  const updateDeduction = (index, field, value) => {
    const updated = [...deductions];
    updated[index] = { ...updated[index], [field]: value };
    setDeductions(updated);
  };

  const handleSave = async () => {
    if (!baseSalary || parseFloat(baseSalary) <= 0) {
      alert(t('drawerModals.compensation.errors.base', 'Please enter a valid base salary'));
      return;
    }

    setSaving(true);
    try {
      const compensationData = {
        base: parseFloat(baseSalary),
        currency,
        paySchedule,
        effectiveDate: effectiveDate || new Date().toISOString().split('T')[0],
        allowances: allowances.filter(a => a.name && a.amount > 0),
        deductions: deductions.filter(d => d.name && d.amount > 0),
        status: currentCompensation?.status || 'active',
        updatedByUID: currentUser?.uid,
        updatedByName: currentUser?.displayName || currentUser?.email || 'HR Team'
      };

      // Save compensation and its minimized retry marker atomically. The marker
      // contains no pay value, but its employee/change metadata is still restricted.
      // Regulatory salary remains protected and is not copied from this browser
      // value; the backend only marks it for nationalization-manager review.
      const workforceUpdates = {
        [`companies/${companyUID}/employees/${employeeId}/compensation`]: compensationData
      };
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId,
        source: 'employee_compensation_edit',
        changeKinds: ['compensation']
      });
      await update(ref(database), workforceUpdates);

      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_compensation_edit',
          changeKinds: ['compensation']
        });
      } catch (syncError) {
        // Compensation is saved; the durable marker stays queued until an
        // authorized online session can record the protected review flag.
        console.warn('Nationalization compensation review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_compensation_edit',
        recalculated: lifecycleSync?.recalculated === true,
        syncPending: !lifecycleSync
      });

      // Send Notification
      try {
        const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
        await set(notifRef, {
          title: t('drawerModals.compensation.notificationTitle', 'Compensation updated'),
          message: t('drawerModals.compensation.notificationMessage', {
            actor: currentUser?.displayName || t('drawerModals.compensation.hrTeam', 'HR team'),
            defaultValue: 'Your compensation details were updated by {{actor}}.'
          }),
          type: 'info',
          timestamp: Date.now(),
          read: false
        });
      } catch (err) { console.error(err); }

      alert(t('drawerModals.compensation.success', 'Compensation updated successfully'));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Update compensation failed:', error);
      alert(t('drawerModals.compensation.failure', 'Failed to update compensation'));
    } finally {
      setSaving(false);
    }
  };

  const calculateTotal = () => {
    const base = parseFloat(baseSalary) || 0;
    const allowanceTotal = allowances.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    const deductionTotal = deductions.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
    return base + allowanceTotal - deductionTotal;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '800px',
          maxHeight: '90vh',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.compensation.title', 'Edit Compensation')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section" style={{ overflowY: 'auto' }}>
          {/* Employee Name */}
          <div className="input-group" style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          {/* Base Salary, Currency, Pay Schedule */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.compensation.base', 'Base Salary')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
              </label>
              <input
                type="number"
                value={baseSalary}
                onChange={(e) => setBaseSalary(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  color: 'var(--app-heading, #031128)'
                }}
              />
            </div>

            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.compensation.currency', 'Currency')}
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  color: 'var(--app-heading, #031128)',
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer'
                }}
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.compensation.schedule', 'Pay Schedule')}
              </label>
              <select
                value={paySchedule}
                onChange={(e) => setPaySchedule(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  color: 'var(--app-heading, #031128)',
                  backgroundColor: '#FFFFFF',
                  cursor: 'pointer'
                }}
              >
                {PAY_SCHEDULES.map(schedule => (
                  <option key={schedule} value={schedule}>
                    {t(`drawerModals.compensation.scheduleOptions.${schedule}`, schedule.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Effective Date */}
          <div className="input-group" style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.compensation.effectiveDate', 'Effective Date')}
            </label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          {/* Allowances Section */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)',
              marginBottom: '16px',
              display: 'block'
            }}>
              {t('drawerModals.compensation.allowances', 'Allowances')}
            </label>

            {allowances.length === 0 ? (
              <div style={{ marginBottom: '12px' }}>
                <button
                  onClick={addAllowance}
                  style={{ '--light-control-fill': '#40EB6E',
                    padding: '8px 16px',
                    backgroundColor: '#40EB6E',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-light-control={"filled"}>
                  <i className="fa-solid fa-plus"></i>
                  {t('drawerModals.compensation.addAllowance', 'Add Allowance')}
                </button>
              </div>
            ) : (
              <>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  marginBottom: '12px'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent'
                      }}>
                        <div className="compensation-table-header">
                          <span>{t('drawerModals.compensation.allowanceName', 'Allowance Name')}</span>
                        </div>
                      </th>
                      <th style={{
                        textAlign: 'left',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent'
                      }}>
                        <div className="compensation-table-header">
                          <span>{t('drawerModals.compensation.amount', 'Amount')}</span>
                        </div>
                      </th>
                      <th style={{
                        textAlign: 'center',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent',
                        width: '80px'
                      }}>
                        <div className="compensation-table-header" style={{ justifyContent: 'center' }}>
                          <span>{t('drawerModals.compensation.delete', 'Delete?')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowances.map((allowance, index) => (
                      <tr key={index}>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: '1px solid rgba(86, 141, 237, 0.7)',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === allowances.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          verticalAlign: 'middle'
                        }}>
                          <input
                            type="text"
                            value={allowance.name}
                            onChange={(e) => updateAllowance(index, 'name', e.target.value)}
                            placeholder={t('drawerModals.compensation.allowanceName', 'Allowance Name')}
                            style={{
                              width: '100%',
                              padding: 0,
                              border: 'none',
                              background: 'transparent',
                              fontFamily: 'Montserrat',
                              fontSize: '16px',
                              color: 'var(--app-heading, #031128)',
                              outline: 'none',
                              borderRadius: 0
                            }}
                            className="compensation-table-input"
                          />
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: '1px solid rgba(86, 141, 237, 0.7)',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === allowances.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          verticalAlign: 'middle'
                        }}>
                          <input
                            type="text"
                            value={allowance.amount}
                            onChange={(e) => {
                              // Only allow numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Ensure only one decimal point
                              const parts = value.split('.');
                              const filteredValue = parts.length > 2
                                ? parts[0] + '.' + parts.slice(1).join('')
                                : value;
                              updateAllowance(index, 'amount', filteredValue === '' ? '' : parseFloat(filteredValue) || 0);
                            }}
                            placeholder="0.00"
                            style={{
                              width: '100%',
                              padding: 0,
                              border: 'none',
                              background: 'transparent',
                              fontFamily: 'Montserrat',
                              fontSize: '16px',
                              color: 'var(--app-heading, #031128)',
                              outline: 'none',
                              borderRadius: 0
                            }}
                            className="compensation-table-input"
                          />
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === allowances.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          textAlign: 'center',
                          verticalAlign: 'middle'
                        }}>
                          <button
                            onClick={() => removeAllowance(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto',
                              width: '100%',
                              height: '100%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                            data-light-control={"quiet"}>
                            <i className="fa-solid fa-times" style={{ color: 'var(--app-tone-eb4040, #EB4040)', fontSize: '16px' }}></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  onClick={addAllowance}
                  style={{ '--light-control-fill': '#40EB6E',
                    padding: '8px 16px',
                    backgroundColor: '#40EB6E',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-light-control={"filled"}>
                  <i className="fa-solid fa-plus"></i>
                  {t('drawerModals.compensation.addAllowance', 'Add Allowance')}
                </button>
              </>
            )}
          </div>

          {/* Deductions Section */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)',
              marginBottom: '16px',
              display: 'block'
            }}>
              {t('drawerModals.compensation.deductions', 'Deductions')}
            </label>

            {deductions.length === 0 ? (
              <div style={{ marginBottom: '12px' }}>
                <button
                  onClick={addDeduction}
                  style={{ '--light-control-fill': '#EB4040',
                    padding: '8px 16px',
                    backgroundColor: '#EB4040',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-light-control={"filled"}>
                  <i className="fa-solid fa-plus"></i>
                  {t('drawerModals.compensation.addDeduction', 'Add Deduction')}
                </button>
              </div>
            ) : (
              <>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  marginBottom: '12px'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent'
                      }}>
                        <div className="compensation-table-header">
                          <span>{t('drawerModals.compensation.deductionName', 'Deduction Name')}</span>
                        </div>
                      </th>
                      <th style={{
                        textAlign: 'left',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent'
                      }}>
                        <div className="compensation-table-header">
                          <span>{t('drawerModals.compensation.amount', 'Amount')}</span>
                        </div>
                      </th>
                      <th style={{
                        textAlign: 'center',
                        padding: 0,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 400,
                        color: 'var(--app-heading, #031128)',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: '2px solid #568ded',
                        background: 'none',
                        backgroundColor: 'transparent',
                        width: '80px'
                      }}>
                        <div className="compensation-table-header" style={{ justifyContent: 'center' }}>
                          <span>{t('drawerModals.compensation.delete', 'Delete?')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.map((deduction, index) => (
                      <tr key={index}>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: '1px solid rgba(86, 141, 237, 0.7)',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === deductions.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          verticalAlign: 'middle'
                        }}>
                          <input
                            type="text"
                            value={deduction.name}
                            onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                            placeholder={t('drawerModals.compensation.deductionName', 'Deduction Name')}
                            style={{
                              width: '100%',
                              padding: 0,
                              border: 'none',
                              background: 'transparent',
                              fontFamily: 'Montserrat',
                              fontSize: '16px',
                              color: 'var(--app-heading, #031128)',
                              outline: 'none',
                              borderRadius: 0
                            }}
                            className="compensation-table-input"
                          />
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: '1px solid rgba(86, 141, 237, 0.7)',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === deductions.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          verticalAlign: 'middle'
                        }}>
                          <input
                            type="text"
                            value={deduction.amount}
                            onChange={(e) => {
                              // Only allow numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Ensure only one decimal point
                              const parts = value.split('.');
                              const filteredValue = parts.length > 2
                                ? parts[0] + '.' + parts.slice(1).join('')
                                : value;
                              updateDeduction(index, 'amount', filteredValue === '' ? '' : parseFloat(filteredValue) || 0);
                            }}
                            placeholder="0.00"
                            style={{
                              width: '100%',
                              padding: 0,
                              border: 'none',
                              background: 'transparent',
                              fontFamily: 'Montserrat',
                              fontSize: '16px',
                              color: 'var(--app-heading, #031128)',
                              outline: 'none',
                              borderRadius: 0
                            }}
                            className="compensation-table-input"
                          />
                        </td>
                        <td style={{
                          padding: '10px 8px',
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderTop: index === 0 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          borderBottom: index === deductions.length - 1 ? 'none' : '1px solid rgba(86, 141, 237, 0.7)',
                          textAlign: 'center',
                          verticalAlign: 'middle'
                        }}>
                          <button
                            onClick={() => removeDeduction(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto',
                              width: '100%',
                              height: '100%'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                            data-light-control={"quiet"}>
                            <i className="fa-solid fa-times" style={{ color: 'var(--app-tone-eb4040, #EB4040)', fontSize: '16px' }}></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  onClick={addDeduction}
                  style={{ '--light-control-fill': '#EB4040',
                    padding: '8px 16px',
                    backgroundColor: '#EB4040',
                    border: 'none',
                    borderRadius: '6px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-light-control={"filled"}>
                  <i className="fa-solid fa-plus"></i>
                  {t('drawerModals.compensation.addDeduction', 'Add Deduction')}
                </button>
              </>
            )}
          </div>

          {/* Net Total */}
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#F8F9FA',
            border: '2px solid #568ded',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.compensation.netTotal', 'Net Total')} ({currency})
              </span>
              <span style={{
                fontFamily: 'Montserrat',
                fontSize: '20px',
                fontWeight: "var(--app-weight-strong, 700)",
                color: '#568ded'
              }}>
                {currency} {calculateTotal().toLocaleString(getUiLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !baseSalary || parseFloat(baseSalary) <= 0}
            style={{ '--light-control-fill': saving || !baseSalary || parseFloat(baseSalary) <= 0 ? '#CCCCCC' : '#568ded',
              padding: '12px 24px',
              backgroundColor: saving || !baseSalary || parseFloat(baseSalary) <= 0 ? '#CCCCCC' : '#568ded',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: saving || !baseSalary || parseFloat(baseSalary) <= 0 ? 'not-allowed' : 'pointer'
            }}
            data-light-control={saving || !baseSalary || parseFloat(baseSalary) <= 0 ? "quiet" : "filled"}>
            {saving ? t('drawerModals.compensation.saving', 'Saving...') : t('drawerModals.compensation.save', 'Save Compensation')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCompensationModal;
